import classNames from 'classnames';
import * as React from 'react';

import { VoiceboxChat } from 'bones/components/molecules/Voicebox/chat/VoiceboxChat';
import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { VoiceboxLanding } from 'bones/components/molecules/Voicebox/landing/VoiceboxLanding';
import { VoiceboxIriPanel } from 'bones/components/molecules/Voicebox/panel/VoiceboxIriPanel';
import { VoiceboxMessagePanel } from 'bones/components/molecules/Voicebox/panel/VoiceboxMessagePanel';
import { Loading } from 'bones/design-system/components/Loading';

export const VoiceboxChatPage: React.VFC = () => {
  const {
    config: { initialAiReasoning },
    testIds,
  } = useVoiceboxSchema();
  const {
    isLoadingConversation,
    isLoadingInitialConversation,
    isLoadingMessage,
    isRefetchingConversation,
    messages,
    sendMessage,
  } = useVoiceboxConversation();
  const {
    catalogProvidersLoading,
    databasesLoading,
    namedGraphsLoading,
    settings,
    lastSettingsLoading,
    lastSettingsRefetching,
  } = useVoiceboxSettings();
  const { currentPanel } = useVoiceboxPanelData();

  const [aiReasoning, setAiReasoning] = React.useState(initialAiReasoning);
  const [query, setQuery] = React.useState('');
  const [lastTypedQuery, setLastTypedQuery] = React.useState('');
  const [queryHistoryIdx, setQueryHistoryIdx] = React.useState(0);
  const [isDataSourcesOpen, setIsDataSourcesOpen] = React.useState(false);
  const [isReasoningOpenById, setIsReasoningOpenById] = React.useState<
    Record<string, boolean>
  >({});

  const sendMessageDisabled = React.useMemo(
    () =>
      isLoadingConversation ||
      isLoadingMessage ||
      isRefetchingConversation ||
      catalogProvidersLoading ||
      databasesLoading ||
      namedGraphsLoading ||
      lastSettingsLoading ||
      lastSettingsRefetching,
    [
      catalogProvidersLoading,
      databasesLoading,
      isLoadingConversation,
      isLoadingMessage,
      isRefetchingConversation,
      lastSettingsLoading,
      lastSettingsRefetching,
      namedGraphsLoading,
    ]
  );

  const handleSendMessage = React.useCallback(
    (message: string) => {
      const trimmed = message.trim();
      setAiReasoning(initialAiReasoning);
      setLastTypedQuery('');
      setQueryHistoryIdx(0);
      setQuery('');
      setIsReasoningOpenById({});
      sendMessage(trimmed, settings, aiReasoning);
    },
    [aiReasoning, initialAiReasoning, sendMessage, settings]
  );

  const isReasoningOpen = React.useCallback(
    (messageId: string | undefined) =>
      isReasoningOpenById[messageId || ''] || false,
    [isReasoningOpenById]
  );

  const setIsReasoningOpen = React.useCallback(
    (messageId: string | undefined) => (isOpen: boolean) => {
      // istanbul ignore else
      if (messageId) {
        setIsReasoningOpenById((prev) => ({
          ...prev,
          [messageId]: isOpen,
        }));
      }
    },
    [setIsReasoningOpenById]
  );

  if (isLoadingInitialConversation) {
    return (
      <div
        className={classNames('vb-voicebox-page', 'vb-voicebox-page--empty')}
      >
        <div data-testid={testIds.drawer.loading.conversationLoad}>
          <Loading size="lg" />
        </div>
      </div>
    );
  }

  if (!messages.length) {
    return (
      <div
        className={classNames('vb-voicebox-page', 'vb-voicebox-page--landing')}
      >
        <VoiceboxLanding
          aiReasoning={aiReasoning}
          lastTypedQuery={lastTypedQuery}
          query={query}
          queryHistoryIdx={queryHistoryIdx}
          sendMessage={handleSendMessage}
          sendMessageDisabled={sendMessageDisabled}
          setAiReasoning={setAiReasoning}
          setLastTypedQuery={setLastTypedQuery}
          setQuery={setQuery}
          setQueryHistoryIdx={setQueryHistoryIdx}
        />
      </div>
    );
  }

  return (
    <div className={classNames('vb-voicebox-page', 'vb-voicebox-page--chat')}>
      {currentPanel && currentPanel.iri ? (
        <VoiceboxIriPanel
          iri={currentPanel.iri}
          isDataSourcesOpen={isDataSourcesOpen}
          messageId={currentPanel.messageId}
          setIsDataSourcesOpen={setIsDataSourcesOpen}
        />
      ) : null}
      {currentPanel && !currentPanel.iri ? (
        <VoiceboxMessagePanel
          isAnalysisOpen={currentPanel.isAnalysisOpen}
          isChartOpen={currentPanel.isChartOpen}
          isDataSourcesOpen={isDataSourcesOpen}
          messageId={currentPanel.messageId}
          setIsDataSourcesOpen={setIsDataSourcesOpen}
        />
      ) : null}
      <VoiceboxChat
        aiReasoning={aiReasoning}
        indicator={<div className="vb-voicebox-chat-indicator" />}
        isReasoningOpen={isReasoningOpen}
        lastTypedQuery={lastTypedQuery}
        query={query}
        queryHistoryIdx={queryHistoryIdx}
        sendMessage={handleSendMessage}
        sendMessageDisabled={sendMessageDisabled}
        setAiReasoning={setAiReasoning}
        setIsReasoningOpen={setIsReasoningOpen}
        setLastTypedQuery={setLastTypedQuery}
        setQuery={setQuery}
        setQueryHistoryIdx={setQueryHistoryIdx}
      />
    </div>
  );
};
